import {Component, Inject, Injector, ViewChild, ChangeDetectionStrategy} from "@angular/core";

import {ReportError} from "app/app.service";
import {CommandTargetType, ExecutionType} from "app/aws-ssm/automation-document/document-execution-dialog/input-parameters/input-parameters.component";
import {AwsSsmRedirectionScope, ConcurrencyOptions, ErrorOptions, ExecuteDocumentState, ExecuteResourcePayload, ExecuteResourceValues, InputParametersState, TagObject, VersionListBatchResponse} from "app/services/domain/aws-ssm-document.service";
import {BaseApplicationComponent} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";

import {Lookup, UtilsService} from "framework/services/utils.service";
import {BaseComponent} from "framework/ui/components";
import {ControlOption} from "framework/ui/control-option";
import {OVERLAY_DATA, OverlayDialogRef} from "framework/ui/overlays/overlay-dialog";
import {OverlayComponent, OverlayConfig} from "framework/ui/overlays/overlay.component";
import {StandardFormComponent} from "framework/ui/overlays/standard-form.component";

@Component({
               templateUrl: "./command-execution-dialog.component.html",
               styleUrls  : ["../document-execution-dialog/document-execution-dialog.component.scss"],
               changeDetection: ChangeDetectionStrategy.Eager,
               standalone : false
           })
export class CommandExecutionDialogComponent extends BaseApplicationComponent
{
    protected readonly CommandTargetType                              = CommandTargetType;
    public ExecutionType                                              = ExecutionType;
    public readonly commandDocument: Models.AwsSsmDocument;
    public commandRequest: Models.AwsSsmExecuteCommandDocumentRequest = Models.AwsSsmExecuteCommandDocumentRequest.newInstance({});
    public inputParametersState: InputParametersState                 = {
        formattedModelValue: "{}",
        currentModelValue  : "{}",
        valid              : false
    };

    public filterOptions: Models.AwsSsmDocumentFilter;
    public bucketOptions: ControlOption<string>[]       = [];
    public instanceOptions: ControlOption<string>[]     = [];
    public roleOptions: ControlOption<string>[]         = [];
    public notificationOptions: ControlOption<string>[] = [];
    public resGroupOptions: ControlOption<string>[]     = [];
    public versionDetails: VersionListBatchResponse;
    public versionRecord: Models.AwsSsmDocumentVersionDetails;
    public versionOptions: ControlOption<string>[]      = [];
    public regionOptions: ControlOption<string>[]       = [];
    public cloudAccountOptions: ControlOption<string>[] = [];
    public versionLoader: boolean;
    public executeLoader: boolean;
    public isPreFillRequired: boolean;
    public executeDocumentObj: ExecuteCommandModel      = {
        concurrencyTarget       : "",
        maxConcurrencyPercentage: "",
        maxErrors               : "",
        maxErrorsPercentage     : "",
        enableS3Bucket          : false,
        enableSnsNotification   : false,
        isInpParamsValid        : true,
        targetObj               : {
            resourceGroup: "",
            instanceList : [],
            tagsList     : [],
            tagKey       : "",
            tagValue     : ""
        }
    };

    @ViewChild(StandardFormComponent) form: StandardFormComponent;

    constructor(inj: Injector,
                public dialogRef: OverlayDialogRef<Models.AwsSsmDocument>,
                @Inject(OVERLAY_DATA) public data: ExecuteDocumentState)
    {
        super(inj);
        this.executeDocumentObj.targetType               = CommandTargetType.Tags;
        const notificationValues: string[]               = UtilsService.getEnumValues(Models.AwsSsmExecuteDocumentNotificationEvents);
        this.commandDocument                             = data.initial;
        this.commandRequest.cloud_account_id             = "";
        this.commandRequest.region                       = "";
        this.commandRequest.cloud_watch_output_config    = Models.AwsSsmCommandDocumentCloudWatchOutputConfig.newInstance({
                                                                                                                              cloud_watch_output_enabled: false,
                                                                                                                              cloud_watch_log_group_name: ""
                                                                                                                          });
        this.filterOptions                               = data.filterValues;
        this.cloudAccountOptions                         = this.filterOptions.cloud_account_details.map(cloud_account => new ControlOption<string>(cloud_account.id, cloud_account.name));
        this.notificationOptions                         = notificationValues.map(eventValue => new ControlOption<string>(eventValue, eventValue));
        this.regionOptions                               = this.filterOptions.regions.map(region => new ControlOption<string>(region, region));
        this.commandRequest.region                       = this.commandDocument.region || this.filterOptions.regions[0];
        this.commandRequest.cloud_account_id             = this.commandDocument.cloud_account_id || this.filterOptions.cloud_account_details[0].id;
        this.executeDocumentObj.concurrencyOption        = ConcurrencyOptions.Targets;
        this.executeDocumentObj.errorsOption             = ErrorOptions.Errors;
        this.executeDocumentObj.concurrencyTarget        = "";
        this.executeDocumentObj.maxConcurrencyPercentage = "";
        this.executeDocumentObj.maxErrorsPercentage      = "";
        this.executeDocumentObj.maxErrors                = "";
        this.initNotificationConfig();
        this.prefillExecute(data?.prefillObj);
        this.initVersionOptions(data?.prefillObj);
    }

    public initNotificationConfig()
    {
        this.commandRequest.notification_config = Models.AwsSsmNotificationConfig.newInstance({
                                                                                                  arn              : "",
                                                                                                  notification_type: Models.AwsSsmCommandNotificationTypes.Command,
                                                                                                  events           : []
                                                                                              });
    }

    get isSelectionReadOnly()
    {
        const scope = this.commandDocument.scope;
        return scope === "Self" || scope === "Private";
    }

    public prefillExecute(prefillVersion?: Models.AwsSsmExecuteCommandDocumentRequest): void
    {
        if (prefillVersion)
        {
            this.isPreFillRequired               = true;
            this.commandRequest.region           = prefillVersion.region;
            this.commandRequest.cloud_account_id = prefillVersion.cloud_account_id;
            this.commandRequest.change_id        = prefillVersion.change_id || "";
            this.commandRequest.comment          = prefillVersion.comment || "";
            this.commandRequest.timeout_seconds  = prefillVersion.timeout_seconds;

            this.executeDocumentObj.enableSnsNotification             = !!prefillVersion.service_role_arn;
            this.commandRequest.service_role_arn                      = prefillVersion.service_role_arn || "";
            this.commandRequest.notification_config.arn               = prefillVersion.notification_config?.arn;
            this.commandRequest.notification_config.events            = prefillVersion.notification_config?.events;
            this.commandRequest.notification_config.notification_type = prefillVersion.notification_config?.notification_type;

            switch (prefillVersion.targets[0].key)
            {
                case "InstanceIds":
                    this.fetchInstance();
                    this.executeDocumentObj.targetType             = CommandTargetType.Instance;
                    this.executeDocumentObj.targetObj.instanceList = prefillVersion.targets[0].values;
                    break;

                case "resource-groups:Name":
                    this.fetchResourceGroup();
                    this.executeDocumentObj.targetType              = CommandTargetType.ResourceGroup;
                    this.executeDocumentObj.targetObj.resourceGroup = prefillVersion.targets[0].values[0];
                    break;

                default:
                    this.executeDocumentObj.targetType = CommandTargetType.Tags;

                    let targetObj = this.executeDocumentObj.targetObj;
                    for (const tagObj of prefillVersion.targets)
                    {
                        if (tagObj.key === "tag-key")
                        {
                            for (const nestedTag of tagObj.values)
                            {
                                targetObj.tagKey   = nestedTag;
                                targetObj.tagValue = "";
                                this.addTargetTags();
                            }
                        }
                        else
                        {
                            targetObj.tagKey   = tagObj.key.split(":")[1];
                            targetObj.tagValue = tagObj.values[0];
                            this.addTargetTags();
                        }
                    }
                    break;
            }

            this.executeDocumentObj.enableS3Bucket = !!prefillVersion.output_s3_bucket_name;
            if (this.executeDocumentObj.enableS3Bucket)
            {
                this.getRoleNames();
            }
            this.getBucketNames(prefillVersion);

            const cloudWatchOutputConfig                      = this.commandRequest.cloud_watch_output_config;
            cloudWatchOutputConfig.cloud_watch_output_enabled = prefillVersion.cloud_watch_output_config?.cloud_watch_output_enabled;
            cloudWatchOutputConfig.cloud_watch_log_group_name = prefillVersion.cloud_watch_output_config?.cloud_watch_log_group_name;

            const maxConcurrency = prefillVersion.max_concurrency || "";
            const maxErrors      = prefillVersion.max_errors || "";
            if (prefillVersion.max_concurrency.includes("%"))
            {
                this.executeDocumentObj.concurrencyOption        = ConcurrencyOptions.Percentage;
                this.executeDocumentObj.maxConcurrencyPercentage = maxConcurrency;
            }
            else
            {
                this.executeDocumentObj.concurrencyOption = ConcurrencyOptions.Targets;
                this.executeDocumentObj.concurrencyTarget = maxConcurrency;
            }
            if (prefillVersion.max_errors.includes("%"))
            {
                this.executeDocumentObj.concurrencyOption   = ConcurrencyOptions.Percentage;
                this.executeDocumentObj.maxErrorsPercentage = maxErrors;
            }
            else
            {
                this.executeDocumentObj.concurrencyOption = ConcurrencyOptions.Targets;
                this.executeDocumentObj.maxErrors         = maxErrors;
            }
        }
    }

    public static async open(comp: BaseComponent,
                             data: ExecuteDocumentState): Promise<Models.AwsSsmDocument>
    {
        return OverlayComponent.open(comp, CommandExecutionDialogComponent, {
            data  : data,
            config: OverlayConfig.dialog({
                                             showCloseButton    : true,
                                             width              : "60vw",
                                             height             : "80vh",
                                             closableViaBackdrop: false
                                         })
        });
    }

    public async initVersionOptions(prefillVersion?: Models.AwsSsmExecuteCommandDocumentRequest): Promise<void>
    {
        this.versionLoader = true;
        try
        {
            this.versionDetails = await this.app.domain.awsSsmDocument.getVersionList(this.commandDocument.sysId);
            this.versionOptions = this.versionDetails.listRecord
                                      .map((versionObj) =>
                                           {
                                               const versionPrefixString = versionObj.document_version === this.commandDocument.document_version ? "- (Default)" : "";
                                               return new ControlOption<string>(versionObj.document_version, `v${versionObj.document_version} ${versionPrefixString}`);
                                           })
                                      .sort((a,
                                             b) => parseInt(a.id) - parseInt(b.id));

            this.commandRequest.document_version = prefillVersion?.document_version || this.commandDocument.document_version;
            this.findVersionRecord(prefillVersion);
        }
        finally
        {
            this.versionLoader = false;
        }
    }

    public findVersionRecord(prefillVersion?: Models.AwsSsmExecuteCommandDocumentRequest): void
    {
        try
        {
            this.versionLoader = true;
            this.versionRecord = this.app.domain.awsSsmDocument.getVersionRecord(this.commandRequest.document_version);
            this.detectChanges();
            this.inputParametersState.valid = true;
            if (this.versionRecord?.parameters)
            {
                this.inputParametersState.valid = false;
                this.resetParams(prefillVersion);
            }
        }
        finally
        {
            this.versionLoader = false;
        }
    }

    public addTargetTags(): void
    {
        const tagValue = this.executeDocumentObj.targetObj.tagValue || "All Values";
        const index    = this.executeDocumentObj.targetObj.tagsList.findIndex(el => el.key === this.executeDocumentObj.targetObj.tagKey);
        if (index != -1)
        {
            this.executeDocumentObj.targetObj.tagsList[index].value += "," + tagValue;
        }
        else
        {
            let tagObj: TagObject = {
                key  : this.executeDocumentObj.targetObj.tagKey,
                value: tagValue
            };
            this.executeDocumentObj.targetObj.tagsList.push(<Models.KeyValuePair>tagObj);
        }
        this.executeDocumentObj.targetObj.tagKey   = "";
        this.executeDocumentObj.targetObj.tagValue = "";
    }

    public removeTags(tagObj: Models.KeyValuePair): void
    {
        let tags    = this.executeDocumentObj.targetObj.tagsList;
        const index = tags.indexOf(tagObj);
        if (index >= 0)
        {
            tags.splice(index, 1);
        }
    }

    public isFormValid(): boolean
    {
        if (!this.form?.form.valid) return false;
        if (!this.executeDocumentObj.isInpParamsValid) return false;
        if (this.versionLoader) return false;
        if (this.executeDocumentObj.targetType === CommandTargetType.Tags && this.executeDocumentObj.targetObj.tagsList.length === 0) return false;
        return true;
    }

    public async resetParams(prefillParam?: Models.AwsSsmExecuteCommandDocumentRequest): Promise<void>
    {
        if (prefillParam?.parameters)
        {
            let currentParameterModelValues: Lookup<string | string[]> = {};
            for (const key in prefillParam.parameters)
            {
                const searchObject = this.versionRecord.parameters.find((obj) => obj.name == key);

                currentParameterModelValues[key] = this.formatParamValuePrefill(searchObject, prefillParam.parameters[key]);
            }
            this.inputParametersState.formattedModelValue = JSON.stringify(prefillParam.parameters);
            this.inputParametersState.currentModelValue   = JSON.stringify(currentParameterModelValues);
        }
        this.inputParametersState.valid = false;
    }

    public formatParamValuePrefill(param: Models.AwsSsmParameter,
                                   paramValue: string[]): string | string[]
    {
        switch (param.type)
        {
            case Models.AwsSsmDocumentParameterType.StringList:
                return paramValue.toString();

            case Models.AwsSsmDocumentParameterType.MapList:
                return paramValue;

            default:
                // during execution AWS expects all the parameters to be send as array, So during rerun need to be formatted
                return paramValue[0];
        }
    }

    public async getBucketNames(prefillBucket?: Models.AwsSsmExecuteCommandDocumentRequest): Promise<void>
    {
        if (this.executeDocumentObj.enableS3Bucket)
        {
            this.executeDocumentObj.bucketNameType     = ExecuteCommandBucketNameType.List;
            const bucketReqObj: ExecuteResourcePayload = {
                type            : ExecuteResourceValues.Bucket,
                region          : "global",
                cloud_account_id: this.commandRequest.cloud_account_id,
                filterOptions   : this.filterOptions
            };

            const bucketSummary = await this.app.domain.awsSsmDocument.fetchInventoryDetails(bucketReqObj);
            this.bucketOptions  = bucketSummary.map(bucket => new ControlOption<string>(bucket.corestack_resource_id, bucket.corestack_resource_id));
            if (prefillBucket)
            {
                const bucketOptionExists = this.bucketOptions.some(bucketOptions => prefillBucket.output_s3_bucket_name
                                                                                    === bucketOptions.id);

                this.executeDocumentObj.bucketNameType    = bucketOptionExists ? ExecuteCommandBucketNameType.List : ExecuteCommandBucketNameType.Text;
                this.commandRequest.output_s3_bucket_name = prefillBucket.output_s3_bucket_name;
                this.commandRequest.output_s3_key_prefix  = prefillBucket.output_s3_key_prefix;
            }
        }
    }

    public async fetchInstance(): Promise<void>
    {
        const instanceReqObj: ExecuteResourcePayload = {
            type            : ExecuteResourceValues.Instance,
            region          : this.commandRequest.region,
            cloud_account_id: this.commandRequest.cloud_account_id,
            filterOptions   : this.filterOptions
        };

        const instanceSummary = await this.app.domain.awsSsmDocument.fetchInventoryDetails(instanceReqObj);
        this.instanceOptions  = instanceSummary.map((instanceList) =>
                                                    {
                                                        const instanceStatus = instanceList.summary_details.instancesSet.item.instanceState.name;
                                                        const instanceId     = instanceList.corestack_resource_id;
                                                        return new ControlOption<string>(instanceId, `${instanceId} (${instanceStatus}) (${instanceList.summary_details.ping_status || "Disabled"})`);
                                                    });
    }

    public async getRoleNames(): Promise<void>
    {
        if (this.executeDocumentObj.enableSnsNotification)
        {
            if (!this.commandRequest.notification_config)
            {
                this.initNotificationConfig();
            }
            this.commandRequest.notification_config.notification_type = Models.AwsSsmCommandNotificationTypes.Command;
            const roleReqObj: ExecuteResourcePayload                  = {
                type            : ExecuteResourceValues.Roles,
                region          : "global",
                cloud_account_id: this.commandRequest.cloud_account_id,
                filterOptions   : this.filterOptions
            };

            const roleSummary = await this.app.domain.awsSsmDocument.fetchInventoryDetails(roleReqObj);
            this.roleOptions  = roleSummary.map(roleObj => new ControlOption<string>(roleObj.summary_details.Arn, roleObj.summary_details.Arn));
        }
    }


    public async fetchResourceGroup()
    {
        const reqObj: ExecuteResourcePayload = {
            type            : ExecuteResourceValues.ResourceGroup,
            region          : this.commandRequest.region,
            cloud_account_id: this.commandRequest.cloud_account_id,
            filterOptions   : this.filterOptions
        };

        const resGrpSummary  = await this.app.domain.awsSsmDocument.fetchInventoryDetails(reqObj);
        this.resGroupOptions = resGrpSummary.map(roleObj => new ControlOption<string>(roleObj.summary_details.GroupName, roleObj.summary_details.GroupName));
    }

    public onDocumentDtlsChange(): void
    {
        switch (this.executeDocumentObj.targetType)
        {
            case CommandTargetType.Instance:
                this.executeDocumentObj.targetObj.instanceList = [];
                this.fetchInstance();
                break;

            case CommandTargetType.ResourceGroup:
                this.executeDocumentObj.targetObj.resourceGroup = "";
                this.fetchResourceGroup();
                break;

            default:
                if (this.executeDocumentObj.bucketNameType === ExecuteCommandBucketNameType.List)
                {
                    this.getBucketNames();
                }
                this.getRoleNames();
                break;
        }
    }

    public resetTargetType(): void
    {
        this.executeDocumentObj.targetObj.resourceGroup = "";
        this.executeDocumentObj.targetObj.instanceList  = [];
        this.executeDocumentObj.targetObj.tagsList      = [];
    }

    @ReportError
    public async executeCommandDocument(): Promise<void>
    {
        this.commandRequest.parameters    = JSON.parse(this.inputParametersState.formattedModelValue);
        this.commandRequest.document_name = this.commandDocument.name;
        for (const param of this.versionRecord.parameters)
        {
            const parameter = this.commandRequest.parameters?.[param.name];
            if (param.type == Models.AwsSsmDocumentParameterType.MapList && parameter)
            {
                for (const [index, value] of parameter.entries())
                {
                    parameter[index] = JSON.stringify(value);
                }
            }
        }
        const isConcurrencyTarget: boolean = this.executeDocumentObj.concurrencyOption === ConcurrencyOptions.Targets;
        const isErrorOptions: boolean      = this.executeDocumentObj.errorsOption === ErrorOptions.Errors;

        this.commandRequest.max_concurrency = isConcurrencyTarget ? this.executeDocumentObj.concurrencyTarget.toString() : this.executeDocumentObj.maxConcurrencyPercentage + "%";
        this.commandRequest.max_errors      = isErrorOptions ? this.executeDocumentObj.maxErrors.toString() : this.executeDocumentObj.maxErrorsPercentage + "%";

        let targetList = [];
        switch (this.executeDocumentObj.targetType)
        {
            case CommandTargetType.Tags:
                const emptyTagValues = this.executeDocumentObj.targetObj.tagsList.filter(tag => tag.value === "All Values");
                if (emptyTagValues?.length > 0)
                {
                    targetList.push(Models.AwsSsmCommandDocumentTarget.newInstance({
                                                                                       key   : "tag-key",
                                                                                       values: emptyTagValues.map(tags => tags.key)
                                                                                   }));
                }
                for (const tagObj of this.executeDocumentObj.targetObj.tagsList)
                {
                    if (tagObj.value != "All Values")
                    {
                        targetList.push(Models.AwsSsmCommandDocumentTarget.newInstance({
                                                                                           key   : "tag:" + tagObj.key,
                                                                                           values: tagObj.value.split(",")
                                                                                       }));
                    }
                }
                break;

            case CommandTargetType.ResourceGroup:
                targetList = [
                    Models.AwsSsmCommandDocumentTarget.newInstance({
                                                                       key   : "resource-groups:Name",
                                                                       values: [this.executeDocumentObj.targetObj.resourceGroup]
                                                                   })
                ];
                break;

            default:
                targetList = [
                    Models.AwsSsmCommandDocumentTarget.newInstance({
                                                                       key   : "InstanceIds",
                                                                       values: this.executeDocumentObj.targetObj.instanceList
                                                                   })
                ];
                break;
        }
        this.commandRequest.targets = targetList;
        if (!this.executeDocumentObj.enableSnsNotification)
        {
            this.commandRequest.notification_config = null;
            this.commandRequest.service_role_arn    = "";
        }
        if (!this.executeDocumentObj.enableS3Bucket)
        {
            this.commandRequest.output_s3_bucket_name = "";
            this.commandRequest.output_s3_key_prefix  = "";
        }
        if (!this.commandRequest.cloud_watch_output_config.cloud_watch_output_enabled)
        {
            this.commandRequest.cloud_watch_output_config.cloud_watch_log_group_name = "";
        }
        this.executeLoader = true;
        try
        {
            await this.app.domain.apis.awsSsmCommand.commandExecute(this.app.domain.users.lastActiveTenantId, this.commandDocument.sysId, this.commandRequest);
            this.app.framework.errors.success("Command Executed Successfully", -1);
            if (this.isPreFillRequired)
            {
                this.dialogRef.close();
            }
            else
            {
                this.app.ui.navigation.go("provider/services/aws-ssm", [AwsSsmRedirectionScope.CommandExecution]);
            }
        }
        finally
        {
            this.executeLoader = false;
        }
    }

    protected readonly ConcurrencyOptions           = ConcurrencyOptions;
    protected readonly ErrorOptions                 = ErrorOptions;
    protected readonly ExecuteCommandBucketNameType = ExecuteCommandBucketNameType;
}

enum ExecuteCommandBucketNameType
{
    List = "List",
    Text = "Text"
}

export interface ExecuteCommandModel
{
    bucketNameType?: ExecuteCommandBucketNameType;
    concurrencyOption?: ConcurrencyOptions;
    concurrencyTarget: string;
    maxConcurrencyPercentage: string;
    errorsOption?: ErrorOptions;
    maxErrors: string;
    maxErrorsPercentage: string;
    isInpParamsValid: boolean;
    targetObj: ExecuteCommandTargetModel;
    targetType?: CommandTargetType;
    enableS3Bucket: boolean;
    enableSnsNotification: boolean;
}

export interface ExecuteCommandTargetModel
{
    resourceGroup: string;
    instanceList: string[];
    tagsList: Models.KeyValuePair[];
    tagKey: string;
    tagValue: string;
}
